import { CalendarEventData } from './calendar.enum';
import { GitHubAction, GitHubWebhookEvent } from './github';
import { MessagingPlatform } from './messagingPlatform.enum';

export enum AttachmentExtractionStatus {
  PENDING = 'PENDING',
  EXTRACTED = 'EXTRACTED',
  UNSUPPORTED = 'UNSUPPORTED',
  FAILED = 'FAILED',
}

export enum RecipientType {
  TO = 'to',
  CC = 'cc',
  BCC = 'bcc',
}

export enum LabelId {
  UNREAD = 'UNREAD',
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  INBOX = 'INBOX',
  IMPORTANT = 'IMPORTANT',
  TRASH = 'TRASH',
  SPAM = 'SPAM',
  CATEGORY_FORUMS = 'CATEGORY_FORUMS',
  CATEGORY_UPDATES = 'CATEGORY_UPDATES',
  CATEGORY_PROMOTIONS = 'CATEGORY_PROMOTIONS',
  CATEGORY_PERSONAL = 'CATEGORY_PERSONAL',
  CATEGORY_SOCIAL = 'CATEGORY_SOCIAL',
  STARRED = 'STARRED',
  YELLOW_STAR = 'YELLOW_STAR',
}

export type Recipient = {
  userId: string;
  email: string;
  name: string;
  type?: RecipientType;
  slackUserId?: string;
  avatarUrl?: string;
  teamsUserId?: string;
  googleChatMemberId?: string;
};

export type StoredMention = {
  platformUserId: string;
  name: string;
};

export type Address = {
  value: string;
  label: string;
  type?: RecipientType;
};

export type UserCommunicationAttachment = {
  fileName: string;
  fileType: string;
  content?: string;
  size?: number;
  previewUrl?: string;
  contentUrl?: string;
  alt?: string;
  s3Key?: string;
  /** Platform-native file id (Slack `file.id`). The durable handle when contentUrl cannot be used. */
  externalId?: string;
  /** Plain text extracted from the attachment for AI context. Populated lazily and cached back to S3. */
  extractedText?: string;
  /** Unix epoch ms when extraction last ran. */
  extractedAt?: number;
  /** Extraction lifecycle status — see AttachmentExtractionStatus. */
  extractionStatus?: AttachmentExtractionStatus;
};

export type UnsubscribeMethod = {
  type: 'one-click-post' | 'mailto' | 'http-get';
  url?: string;
  emailAddress?: string;
  subject?: string;
};

export type UserCommunicationKeys = {
  userId: string; // PK -> It can be the teamId for team-scoped communications or the actual userId for user-scoped communications, depending on the use case and platform.
  messageId: string; // SK
};

export type UserCommunicationFields = {
  createdAt: number;
  userEmailAddress?: string;
  author: string;
  authorUserId: string;
  authorResourceId?: string;
  /**
   * Keyed HMAC digest of the author's platform identity (e.g. a Slack user
   * id) — kept comparable so the executor can match an author across rows
   * without a decrypt. The raw platform id is never stored in the clear.
   */
  authorPlatformIdHmac?: string;
  authorEmail?: string;
  authorAvatarUrl?: string;
  channel?: string;
  channelName?: string;
  channelType?: string;
  threadMessageId?: string;
  // Readable: a structural boolean the engine branches on (thread grouping), no user content. Google Chat only; absent on rows written before the field existed.
  isThreadReply?: boolean;
  threadTimestamp?: number;
  messagePreview?: string;
  messageText?: string;
  messageRawText?: string;
  recipients?: Recipient[];
  // The recipient list serialized for sealing — the envelope transform only
  // moves flat strings, so `recipients` folds to this on write and is rebuilt
  // on read. Exists on the stored row, never on a value a caller builds.
  recipientsJson?: string;
  mentions?: StoredMention[];
  // Same fold as `recipientsJson`, for `mentions`: ids and names that identify people.
  mentionsJson?: string;
  subject?: string;
  platform?: MessagingPlatform;
  messageUrl?: string;
  labelIds?: LabelId[];
  internetMessageId?: string;
  s3Key?: string;
  inboxEntryIds?: string[];
  draftId?: string;
  draftType?: string;
  slackWorkspaceId?: string;
  githubInstallationId?: string;
  /** Repository fullName `owner/repo`, matched by trigger repositories. Absent on rows ingested before the repository filter existed. */
  githubRepository?: string;
  githubEventName?: GitHubWebhookEvent;
  githubAction?: GitHubAction;
  /** Git ref for `release` (tag) and `push` (branch) events, matched by trigger refPatterns. */
  githubRef?: string;
  /** `push` only: whether githubRef is the repo's default branch. Lets the flow matcher reproduce the ingest gate's "empty refPatterns = default branch" rule, which needs repo state the matcher cannot see. */
  githubIsDefaultBranch?: boolean;
  /** Review resource name `accounts/{a}/locations/{l}/reviews/{r}` — the reply step (GbpReplyReviewStepConfig.reviewName) falls back to this when no override is given. */
  gbpReviewName?: string;
  /** Loose string (not the apps/api-only GbpNotificationType enum — libs/models cannot depend on apps/api utils): `'NEW_REVIEW' | 'UPDATED_REVIEW'`. */
  gbpNotificationType?: string;
  gbpRating?: number;
  gbpAuthorDisplayName?: string;
  /** Location resource name `accounts/{a}/locations/{l}`. */
  gbpLocationName?: string;
  /** Existing reply text at ingest time, so a later reply step can tell a reply already exists. */
  gbpReplyText?: string;
  gbpReviewCreateTime?: string;
  gbpReviewUpdateTime?: string;
  teamsWorkspaceId?: string;
  /** Microsoft account that received the message. Readable: it is the SK of MicrosoftSyncDataV2, and the trigger matcher compares it directly (encryption.md exception 1 — a key/comparable value). */
  teamsHomeAccountId?: string;
  whatsAppPhoneNumberId?: string;
  /**
   * Counterpart's WhatsApp phone number, kept alongside the BSUID in
   * `authorUserId` while Meta still delivers both. Absent once the counterpart
   * adopts a username and the number stops being included.
   */
  whatsAppWaId?: string;
  /**
   * Deterministic HMAC-SHA256 digest of `whatsAppWaId` (`hmacPhoneNumber`,
   * `utils/piiHmac.ts`) — NOT sealed, rides in the clear beside the sealed
   * value. `whatsAppWaId` is envelope-encrypted with a per-row key, so it
   * cannot be compared across rows; `getWhatsAppBsuidByWaId`'s
   * FilterExpression matches on this digest instead.
   */
  whatsAppWaIdHmac?: string;
  /**
   * Counterpart's WhatsApp username handle. Display only — it is mutable and
   * cannot address a send, so it is never an identity key.
   */
  whatsAppUsername?: string;
  teamId?: string;
  igAccountId?: string;
  /** Instagram: id of the replied-to story. Present only when channelType is a story reply. */
  storyId?: string;
  /** Instagram: signed CDN url of the replied-to story. PERISHABLE — expires within hours, never treat as a durable link. */
  storyUrl?: string;
  facebookPageId?: string;
  slackThreadTimestamp?: string;
  isHistoricalSync?: boolean;
  isBot?: boolean;
  isSlackHuddle?: boolean;
  attachments?: UserCommunicationAttachment[];
  threadMessages?: UserCommunication[];
  hasUnsubscribeLink?: boolean;
  unsubscribeMethod?: UnsubscribeMethod;
  isUnsubscribed?: boolean;
  category?: number;
  calendarEventData?: CalendarEventData;
};

export type UserCommunicationContentDetails = {
  messageText: string;
  messageRawText: string;
  attachments: UserCommunicationAttachment[];
};

export type UserCommunication = UserCommunicationKeys & UserCommunicationFields;

export type OptimizedUserCommunication = Pick<
  UserCommunication,
  | 'userId'
  | 'threadMessageId'
  | 'createdAt'
  | 'messageId'
  | 'draftId'
  | 'labelIds'
  | 'inboxEntryIds'
  | 'platform'
>;
